import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { apiGet, apiDelete, apiPatch } from '../services/api';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import {
  Building2, ArrowLeft, Calendar, MapPin, X, Check, Clock
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';

export default function MyBookingsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadBookings = useCallback(async () => {
    try {
      const data = await apiGet('/bookings/me');
      setBookings(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadBookings(); }, [loadBookings]);

  const today = format(new Date(), 'yyyy-MM-dd');
  const upcoming = bookings.filter(b => b.date >= today && b.status !== 'cancelled');
  const past = bookings.filter(b => b.date < today || b.status === 'cancelled');

  const handleCancel = async (id) => {
    try {
      await apiDelete(`/bookings/${id}`);
      toast.success('Booking cancelled');
      loadBookings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    }
  };

  const handleCheckin = async (id) => {
    try {
      await apiPatch(`/bookings/${id}/checkin`);
      toast.success('Checked in!');
      loadBookings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Check-in failed');
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto p-4 md:p-6">
        <div className="flex items-center gap-3 mb-4 md:mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate('/')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl md:text-2xl font-bold">My Bookings</h1>
            <p className="text-xs md:text-sm text-muted-foreground">{user?.display_name}</p>
          </div>
        </div>

        <Tabs defaultValue="upcoming">
          <TabsList>
            <TabsTrigger value="upcoming">Upcoming ({upcoming.length})</TabsTrigger>
            <TabsTrigger value="past">Past ({past.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="upcoming" className="mt-4 space-y-3">
            {upcoming.length === 0 && (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  <Calendar className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>No upcoming bookings</p>
                  <Button variant="outline" className="mt-3" onClick={() => navigate('/')}>
                    Book a Desk
                  </Button>
                </CardContent>
              </Card>
            )}
            {upcoming.map(b => (
              <Card key={b.id}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <MapPin className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <p className="font-semibold">{b.desk_label}</p>
                        <p className="text-sm text-muted-foreground">{b.floor_name}</p>
                        <p className="text-sm text-muted-foreground">{b.date}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={b.status === 'checked_in' ? 'default' : 'secondary'}>
                        {b.status}
                      </Badge>
                      {b.status === 'confirmed' && (
                        <>
                          <Button variant="outline" size="sm" onClick={() => handleCheckin(b.id)}>
                            <Check className="h-3.5 w-3.5 mr-1" /> Check In
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleCancel(b.id)}>
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>

          <TabsContent value="past" className="mt-4 space-y-3">
            {past.length === 0 && (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  <Clock className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>No past bookings</p>
                </CardContent>
              </Card>
            )}
            {past.map(b => (
              <Card key={b.id} className="opacity-70">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">{b.desk_label} - {b.floor_name}</p>
                      <p className="text-sm text-muted-foreground">{b.date}</p>
                    </div>
                    <Badge variant="outline">{b.status}</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
